import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  DestroyRef,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputMaskModule } from 'primeng/inputmask';

import { CommonModule } from '@angular/common';
import { BackButtonComponent } from '@shared/components/back-button/back-button.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EfetivoService,
  FuncaoService,
  CategoriaService,
  TurnoService,
} from '@core/services/domain-services';
import { NifService } from '@core/services/nif.service';
import { compressImageIfNeeded } from '@shared/utils/compress-image.util';
import { finalize, forkJoin } from 'rxjs';
import { DrawerModule } from 'primeng/drawer';
import {
  GeneroEnum,
  EstadoEfetivoEnum,
  ParentescoEnum,
} from '@core/models/models';

interface BankOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-criar-efetivo',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    Select,
    DatePicker,
    InputNumberModule,

    CommonModule,
    BackButtonComponent,
    DrawerModule,
    InputMaskModule,
  ],

  templateUrl: './criar-efetivo.component.html',
  styleUrl: './criar-efetivo.component.scss',
})
export class CriarEfetivoComponent implements OnInit, OnDestroy {
  loading = false;
  private submitted = false;
  loadingDropdowns = false;
  biSearching = false;
  private biAutoFilled = false;

  todayDate = new Date();
  maxDate = new Date();
  ibanMask: string = '';

  selectedFoto: File | null = null;
  fotoPreviewUrl: string | null = null;

  @ViewChild('cameraVideo') cameraVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('cameraCanvas') cameraCanvas!: ElementRef<HTMLCanvasElement>;
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  cameraOpen = false;
  capturedPhotoUrl: string | null = null;
  private mediaStream: MediaStream | null = null;

  selectedFiles: Record<string, File | null> = {
    certificadoFormacao: null,
    registroCriminal: null,
    atestadoMedico: null,
  };

  fileInfo: Record<string, { name: string; size: string }> = {};

  private readonly fb = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly efetivoService = inject(EfetivoService);
  private readonly funcaoService = inject(FuncaoService);
  private readonly categoriaService = inject(CategoriaService);
  private readonly turnoService = inject(TurnoService);
  private readonly nifSvc = inject(NifService);
  private readonly destroyRef = inject(DestroyRef);

  dadosPessoaisForm!: FormGroup;
  dadosProfissionaisForm!: FormGroup;
  documentacaoForm!: FormGroup;

  generos = [
    { label: 'Masculino', value: 1 },
    { label: 'Feminino', value: 2 },
  ];

  angolanBanks: BankOption[] = [
    { label: 'BAI - Banco Angolano de Investimentos', value: 'BAI' },
    { label: 'BFA - Banco de Fomento Angola', value: 'BFA' },
    { label: 'BPC - Banco de Poupança e Crédito', value: 'BPC' },
    { label: 'BIC - Banco BIC', value: 'BIC' },
    { label: 'BCI - Banco de Comércio e Indústria', value: 'BCI' },
    { label: 'BCGA - Banco Caixa Geral Angola', value: 'BCGA' },
    { label: 'BCA - Banco Comercial Angolano', value: 'BCA' },
    { label: 'BDA - Banco de Desenvolvimento de Angola', value: 'BDA' },
    { label: 'BMA - Banco Millennium Atlântico', value: 'BMA' },
    { label: 'BSOL - Banco Sol', value: 'BSOL' },
    { label: 'YETU - Banco YETU', value: 'YETU' },
    { label: 'BANC - Banco Angolano de Negócios e Comércio', value: 'BANC' },
    { label: 'BMF - Banco BAI Microfinanças', value: 'BMF' },
    { label: 'BCH - Banco Comercial do Huambo', value: 'BCH' },
    { label: 'SBA - Standard Bank Angola', value: 'SBA' },
  ];

  turnos: { label: string; value: any }[] = [];

  private readonly mockTurnos = [
    { label: 'Diurno (06h–18h)', value: 'DIURNO' },
    { label: 'Noturno (18h–06h)', value: 'NOTURNO' },
    { label: 'Integral (24 horas)', value: '24_HORAS' },
  ];

  parentescos = [
    { label: 'Cônjuge', value: ParentescoEnum.CONJUGE },
    { label: 'Progenitor', value: ParentescoEnum.PROGENITOR },
    { label: 'Irmão/Irmã', value: ParentescoEnum.IRMAO },
    { label: 'Filho/Filha', value: ParentescoEnum.FILHO },
    { label: 'Outro', value: ParentescoEnum.OUTRO },
  ];

  funcoes: any[] = [];
  categorias: any[] = [];

  bairros = [
    { label: 'Maianga', value: 'Maianga' },
    { label: 'Ingombota', value: 'Ingombota' },
    { label: 'Samba', value: 'Samba' },
    { label: 'Talatona', value: 'Talatona' },
    { label: 'Belas', value: 'Belas' },
    { label: 'Viana', value: 'Viana' },
    { label: 'Cazenga', value: 'Cazenga' },
    { label: 'Cacuaco', value: 'Cacuaco' },
    { label: 'Rangel', value: 'Rangel' },
    { label: 'Kilamba', value: 'Kilamba' },
    { label: 'Camama', value: 'Camama' },
    { label: 'Luanda Centro', value: 'Luanda Centro' },
  ];

  ngOnInit(): void {
    this.maxDate.setFullYear(this.todayDate.getFullYear() - 18);
    this.initForms();
    this.loadDropdowns();

    this.dadosPessoaisForm
      .get('numBI')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.biAutoFilled) {
          this.biAutoFilled = false;
          this.dadosPessoaisForm.get('nome')?.setValue('');
        }
      });
  }

  onBancoChange(event: any): void {
    const bankCodeMap: Record<string, string> = {
      BAI: '0040',
      BFA: '0006',
      BPC: '0010',
      BIC: '0051',
      BCI: '0005',
      BCGA: '0004',
      BCA: '0043',
      BDA: '0054',
      BMA: '0055',
      BSOL: '0044',
      YETU: '0066',
      BANC: '0053',
      BMF: '0048',
      BCH: '0059',
      SBA: '0060',
    };
    const bank = event.value;
    const prefix = bankCodeMap[bank] || '';
    this.ibanMask = prefix ? `AO06 ${prefix} 9999 9999 9999 9999 9` : '';
    const ibanControl = this.dadosProfissionaisForm.get('Iban');
    if (prefix) {
      ibanControl?.enable();
    } else {
      ibanControl?.disable();
    }
    this.dadosProfissionaisForm.patchValue({
      Iban: null,
    });
  }

  initDates(): void {
    this.todayDate = new Date();
    this.maxDate = new Date();
    this.maxDate.setFullYear(this.maxDate.getFullYear() - 18);
  }

  closeDrawer() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  loadDropdowns(): void {
    this.loadingDropdowns = true;
    forkJoin({
      funcoes: this.funcaoService.getAll({ pageSize: 1000 }),
      categorias: this.categoriaService.getAll({ pageSize: 1000 }),
      turnos: this.turnoService.getAll({ pageSize: 100 }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.funcoes = (res.funcoes.data || []).map((f: any) => ({
            id: f.id,
            nome: f.designacao,
          }));
          this.categorias = (res.categorias.data || []).map((c: any) => ({
            id: c.id,
            nome: c.designacao,
          }));
          this.turnos = res.turnos.data?.length
            ? res.turnos.data.map((t: any) => ({
                label: t.designacao,
                value: t.id,
              }))
            : this.mockTurnos;
          this.loadingDropdowns = false;
        },
        error: () => {
          this.funcoes = [
            { id: 1, nome: 'Agente de Segurança' },
            { id: 2, nome: 'Supervisor' },
            { id: 3, nome: 'Coordenador' },
            { id: 4, nome: 'Gestor de Operações' },
          ];
          this.categorias = [
            { id: 1, nome: 'Classe A' },
            { id: 2, nome: 'Classe B' },
            { id: 3, nome: 'Classe C' },
            { id: 4, nome: 'Classe D' },
          ];
          this.turnos = this.mockTurnos;
          this.loadingDropdowns = false;
        },
      });
  }

  initForms(): void {
    this.dadosPessoaisForm = this.fb.group({
      nome: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ],
      ],
      numBI: ['', [Validators.required]],
      dataNascimento: ['', [Validators.required, this.ageValidator(18)]],
      genero: [null, [Validators.required]],
      numTelefone: [
        '',
        [Validators.required, Validators.pattern(/^9[0-9]{8}$/)],
      ],
      email: ['', [Validators.email]],
      modara: ['', [Validators.required]],
    });

    this.dadosProfissionaisForm = this.fb.group({
      idFuncao: [null, [Validators.required]],
      idCategoria: [null, [Validators.required]],
      dataAdmissao: ['', [Validators.required]],
      codigoMec: [''],
      turno: [null],
      salario: [null],
      Iban: [{ value: null, disabled: true }],
      Banco: [null],
    });

    this.documentacaoForm = this.fb.group({
      certificadoFormacao: [null],
      registroCriminal: [null],
      atestadoMedico: [null],
      nomeContactoEmergencia: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.pattern(/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/),
        ],
      ],
      numTelContactoEmergencia: [
        '',
        [Validators.required, Validators.pattern(/^9[0-9]{8}$/)],
      ],
      parentescoContactoEmergencia: ['', [Validators.required]],
    });
  }

  get podeGuardar(): boolean {
    return (
      !!this.dadosPessoaisForm &&
      !!this.dadosProfissionaisForm &&
      !!this.documentacaoForm &&
      this.dadosPessoaisForm.valid &&
      this.dadosProfissionaisForm.valid &&
      this.documentacaoForm.valid
    );
  }

  searchBI(): void {
    const bi = this.dadosPessoaisForm.get('numBI')?.value?.trim();
    if (!bi) {
      this.messageService.add({
        severity: 'warn',
        summary: 'BI em falta',
        detail: 'Introduza o número do BI antes de pesquisar.',
        life: 3000,
      });
      return;
    }
    this.biSearching = true;
    this.nifSvc
      .lookup(bi)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (c) => {
          this.dadosPessoaisForm.patchValue({ nome: c.nome });
          this.biAutoFilled = true;
          this.messageService.add({
            severity: 'success',
            summary: 'BI encontrado',
            detail: `Nome preenchido: ${c.nome}`,
            life: 3000,
          });
          this.biSearching = false;
        },
        error: (error) => {
          console.log(error);
          this.messageService.add({
            severity: 'info',
            summary: 'BI não encontrado',
            detail:
              error.retorno?.mensagem ||
              'Nenhum registo encontrado. Preencha o nome manualmente.',
            life: 4000,
          });
          this.biSearching = false;
        },
      });
  }

  ageValidator(minAge: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const birthDate = new Date(control.value);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      return age >= minAge
        ? null
        : { minAge: { requiredAge: minAge, actualAge: age } };
    };
  }

  async onFotoSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];

    if (file.size > 5 * 1024 * 1024) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Foto muito grande. Máximo 5MB.',
      });
      input.value = '';
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Formato inválido. Use JPG, PNG ou WEBP.',
      });
      input.value = '';
      return;
    }
    const compressedFile = await compressImageIfNeeded(file);
    this.selectedFoto = compressedFile;
    const reader = new FileReader();
    reader.onload = () => {
      this.fotoPreviewUrl = reader.result as string;
    };
    reader.readAsDataURL(compressedFile);
  }

  removeFoto(): void {
    this.selectedFoto = null;
    this.fotoPreviewUrl = null;
  }

  openCamera(): void {
    this.cameraOpen = true;
    this.capturedPhotoUrl = null;
    setTimeout(() => this.startStream(), 100);
  }

  private startStream(): void {
    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 640 },
        },
        audio: false,
      })
      .then((stream) => {
        this.mediaStream = stream;
        const video = this.cameraVideo?.nativeElement;
        if (video) {
          video.srcObject = stream;
          video.play();
        }
      })
      .catch(() => {
        this.messageService.add({
          severity: 'error',
          summary: 'Câmara indisponível',
          detail:
            'Não foi possível aceder à câmara. Verifique as permissões do browser.',
          life: 5000,
        });
        this.cameraOpen = false;
      });
  }

  capturePhoto(): void {
    const video = this.cameraVideo?.nativeElement;
    const canvas = this.cameraCanvas?.nativeElement;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 640;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    this.capturedPhotoUrl = canvas.toDataURL('image/jpeg', 0.9);
    this.stopStream();
  }

  retakePhoto(): void {
    this.capturedPhotoUrl = null;
    setTimeout(() => this.startStream(), 100);
  }

  async confirmPhoto(): Promise<void> {
    if (!this.capturedPhotoUrl) return;
    const arr = this.capturedPhotoUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    const file = new File([u8arr], `foto-camera-${Date.now()}.jpg`, {
      type: mime,
    });

    if (file.size > 5 * 1024 * 1024) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Foto muito grande. Tente novamente.',
        life: 3000,
      });
      return;
    }
    const compressedFile = await compressImageIfNeeded(file);
    this.selectedFoto = compressedFile;
    const reader = new FileReader();
    reader.onload = () => {
      this.fotoPreviewUrl = reader.result as string;
    };
    reader.readAsDataURL(compressedFile);
    this.closeCamera();
  }

  closeCamera(): void {
    this.stopStream();
    this.cameraOpen = false;
    this.capturedPhotoUrl = null;
  }

  private stopStream(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => t.stop());
      this.mediaStream = null;
    }
  }

  ngOnDestroy(): void {
    this.stopStream();
  }

  markAllFieldsAsTouched(): void {
    Object.keys(this.dadosPessoaisForm.controls).forEach((k) =>
      this.dadosPessoaisForm.get(k)?.markAsTouched(),
    );
    Object.keys(this.dadosProfissionaisForm.controls).forEach((k) =>
      this.dadosProfissionaisForm.get(k)?.markAsTouched(),
    );
    Object.keys(this.documentacaoForm.controls).forEach((k) =>
      this.documentacaoForm.get(k)?.markAsTouched(),
    );
  }

  hasFieldError(
    form: FormGroup,
    fieldName: string,
    errorName: string,
  ): boolean {
    const control = form.get(fieldName);
    return !!(
      control?.hasError(errorName) &&
      (control.dirty || control.touched)
    );
  }

  onSubmit(): void {
    if (
      !(
        this.dadosPessoaisForm.valid &&
        this.dadosProfissionaisForm.valid &&
        this.documentacaoForm.valid
      )
    ) {
      this.markAllFieldsAsTouched();
      const secoesInvalidas = [
        !this.dadosPessoaisForm.valid && 'Dados Pessoais',
        !this.dadosProfissionaisForm.valid && 'Dados Profissionais',
        !this.documentacaoForm.valid && 'Contacto de Emergência',
      ].filter(Boolean);
      this.messageService.add({
        severity: 'error',
        summary: 'Erro de Validação',
        detail: `Preencha todos os campos obrigatórios em: ${secoesInvalidas.join(', ')}.`,
        life: 5000,
      });
      return;
    }
    if (this.submitted || this.loading) return;
    this.submitted = true;
    this.loading = true;

    const fd = new FormData();
    fd.append('Nome', this.dadosPessoaisForm.get('nome')?.value ?? '');
    fd.append('NumBI', this.dadosPessoaisForm.get('numBI')?.value ?? '');
    const ibanValue = this.dadosProfissionaisForm.get('Iban')?.value;
    const ibanVal =
      typeof ibanValue === 'string' ? ibanValue.replace(/\s/g, '') : '';
    if (ibanVal) fd.append('Iban', ibanVal);

    const bancoVal = this.dadosProfissionaisForm.get('Banco')?.value;
    if (bancoVal) fd.append('Banco', bancoVal);
    const turnoVal = this.dadosProfissionaisForm.get('turno')?.value;
    if (turnoVal) {
      fd.append('Turno', String(turnoVal).toUpperCase());
    }
    const salarioVal = this.dadosProfissionaisForm.get('salario')?.value;
    if (salarioVal !== null && salarioVal !== undefined && salarioVal !== '') {
      fd.append('Salario', String(salarioVal));
    }
    fd.append(
      'DataNascimento',
      this.formatDate(this.dadosPessoaisForm.get('dataNascimento')?.value),
    );
    fd.append('Genero', this.dadosPessoaisForm.get('genero')?.value ?? '');
    fd.append(
      'NumTelefone',
      this.dadosPessoaisForm.get('numTelefone')?.value ?? '',
    );
    const email = this.dadosPessoaisForm.get('email')?.value;
    if (email) fd.append('Email', email);
    const modara = this.dadosPessoaisForm.get('modara')?.value;
    if (modara) fd.append('Modara', modara);
    fd.append(
      'IdFuncao',
      String(this.dadosProfissionaisForm.get('idFuncao')?.value ?? ''),
    );
    fd.append(
      'IdCategoria',
      String(this.dadosProfissionaisForm.get('idCategoria')?.value ?? ''),
    );
    fd.append(
      'DataAdmissao',
      this.formatDate(this.dadosProfissionaisForm.get('dataAdmissao')?.value),
    );
    const codigoMec = this.dadosProfissionaisForm.get('codigoMec')?.value;
    if (codigoMec) fd.append('CodigoMec', codigoMec.trim());
    fd.append(
      'NomeContactoEmergencia',
      this.documentacaoForm.get('nomeContactoEmergencia')?.value ?? '',
    );
    fd.append(
      'NumTelContactoEmergencia',
      this.documentacaoForm.get('numTelContactoEmergencia')?.value ?? '',
    );
    const parentescoVal = this.documentacaoForm.get(
      'parentescoContactoEmergencia',
    )?.value;
    const parentescoLabelMap: Record<number, string> = {
      1: 'CONJUGE',
      2: 'PROGENITOR',
      3: 'IRMAO',
      4: 'FILHO',
      5: 'OUTRO',
    };
    const parentescoDesc = parentescoLabelMap[Number(parentescoVal)] || 'OUTRO';

    fd.append('ParentescoContactoEmergencia', parentescoDesc);
    if (parentescoVal !== null && parentescoVal !== undefined) {
      fd.append('EfetivoGrauParentescoEnum', String(parentescoVal));
    }
    if (this.selectedFoto)
      fd.append('fotografia', this.selectedFoto, this.selectedFoto.name);
    if (this.selectedFiles['certificadoFormacao'])
      fd.append(
        'certificadoFormacao',
        this.selectedFiles['certificadoFormacao']!,
      );
    if (this.selectedFiles['registroCriminal'])
      fd.append('registroCriminal', this.selectedFiles['registroCriminal']!);
    if (this.selectedFiles['atestadoMedico'])
      fd.append('atestadoMedico', this.selectedFiles['atestadoMedico']!);

    this.efetivoService
      .store(fd)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
          this.submitted = false;
        }),
      )
      .subscribe({
        next: (res: any) => {
          const ok =
            res?.retorno?.codigo === 200 ||
            res?.retorno?.codigo === 201 ||
            res?.id;
          if (ok || !res?.retorno) {
            this.messageService.add({
              severity: 'success',
              summary: 'Sucesso',
              detail: 'Efetivo cadastrado com sucesso!',
            });
            this.resetForm();
            this.saved.emit();
            this.closeDrawer();
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: 'Aviso',
              detail: res?.retorno?.mensagem || 'Verifique o resultado.',
            });
          }
        },
        // A mensagem da API é apresentada globalmente pelo errorInterceptor.
        error: () => undefined,
      });
  }

  formatDate(date: any): string {
    if (!date) return '';
    if (typeof date === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(date)) return date.slice(0, 10);
      date = new Date(date);
    }
    if (date instanceof Date && !isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  }

  resetForm(): void {
    this.dadosPessoaisForm.reset();
    this.dadosProfissionaisForm.reset();
    this.documentacaoForm.reset();
    this.selectedFoto = null;
    this.fotoPreviewUrl = null;
    this.selectedFiles = {
      certificadoFormacao: null,
      registroCriminal: null,
      atestadoMedico: null,
    };
    this.fileInfo = {};
  }

  async onFileSelect(event: Event, fieldName: string): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    if (file.size > 5 * 1024 * 1024) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Arquivo muito grande. Máximo 5MB.',
      });
      input.value = '';
      return;
    }
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Formato não permitido. Use PDF, JPG ou PNG.',
      });
      input.value = '';
      return;
    }
    const finalFile = file.type.startsWith('image/')
      ? await compressImageIfNeeded(file)
      : file;
    this.selectedFiles[fieldName] = finalFile;
    this.fileInfo[fieldName] = {
      name: finalFile.name,
      size: this.formatFileSize(finalFile.size),
    };
    this.documentacaoForm.get(fieldName)?.setValue(finalFile);
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
